import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";

import {
  fetchData,
  postQuery,
  searchfetchQueries,
} from "../../Requirement/services/RequirementServices";

const ProformaInvoiceForm = ({
  formData,
  setFormData,
  errors,
  setErrors,
  isEditMode,
  handleSubmit,
  allProjectList,
}) => {
  const [strategicPartnerInfo, setStrategicPartnerInfo] = useState([]);
  const [productInfo, setProductInfo] = useState([]);

  const getStrategicPartnerInfo = useCallback(async (partnerID) => {
    try {
      const response = await fetchData({
        target: "listAMTPLUsers",
        userOrder: "AMTPLUserID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { AMTPLUserID: partnerID },
      });
      setStrategicPartnerInfo(response.items[0] || []);
    } catch (error) {
      console.error("Error loading buyer info:", error);
      setStrategicPartnerInfo([]);
    }
  }, []);

  const getProductInfo = useCallback(async (productID) => {
    try {
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { ProductID: productID },
      });
      setProductInfo(response.items[0] || []);
    } catch (error) {
      console.error("Error loading buyer info:", error);
      setProductInfo([]);
    }
  }, []);

  // New useEffect to handle initial data fetching in edit mode
  useEffect(() => {
    if (isEditMode && formData.ProjectID) {
      const selectedProject = allProjectList.find(
        (project) => project.ProjectID === formData.ProjectID
      );
      if (selectedProject) {
        if (selectedProject.StrategicPartnerID) {
          getStrategicPartnerInfo(selectedProject.StrategicPartnerID);
        }
        if (selectedProject.ProductID) {
          getProductInfo(selectedProject.ProductID);
        }
      }
    }
  }, [isEditMode, formData.ProjectID, allProjectList, getStrategicPartnerInfo, getProductInfo]);

  useEffect(() => {
    if (strategicPartnerInfo && Object.keys(strategicPartnerInfo).length > 0) {
      let partnerFname = strategicPartnerInfo.FirstName || "";
      let partnerLname = strategicPartnerInfo.LastName || "";
      let partnerCompanyName = strategicPartnerInfo.CompanyName || "";
      let partnerCompanyAddress1 = strategicPartnerInfo.CompanyAddress1 || "";
      let partnerCompanyAddress2 = strategicPartnerInfo.CompanyAddress2 || "";
      let partnerCompanyAddress3 = strategicPartnerInfo.CompanyAddress3 || "";
      let partnerCompanyCity = strategicPartnerInfo.CompanyCity || "";
      let partnerCompanyState = strategicPartnerInfo.CompanyState || "";
      let partnerCompanyCountry = strategicPartnerInfo.CompanyCountry || "";
      let partnerCompanyZipCode = strategicPartnerInfo.CompanyZipCode || "";
      let partnerCompanyGSTIN = strategicPartnerInfo.CompanyGSTIN || "";
      let partnerCAddress =
        partnerCompanyName +
        " " +
        partnerCompanyAddress1 +
        " " +
        partnerCompanyAddress2 +
        " " +
        partnerCompanyAddress3 +
        " " +
        partnerCompanyCity +
        " -" +
        partnerCompanyZipCode +
        " " +
        partnerCompanyState;
      setFormData((prev) => ({
        ...prev,
        PlaceOfSupply: strategicPartnerInfo.State || "",
        StrategicPartner: partnerFname + " " + partnerLname,
        ShipTo: partnerCAddress,
      }));
    }
    if (productInfo && Object.keys(productInfo).length > 0) {
      setFormData((prev) => ({
        ...prev,
        ProductName: productInfo.ProductName || "",
        Grade: productInfo.Grade || "",
        HSCode: productInfo.HsCode || "",
      }));
    }
  }, [strategicPartnerInfo, productInfo, setFormData]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const readOnlyFields = [
      "Unit",
      "SubTotal",
      "IgstPercent",
      "CgstPercent",
      "SgstPercent",
      "TotalAmount",
      "ProductName",
      "Grade",
      "HSCode",
    ];

    // Allow changes to PricePerUnit and tax fields for calculations
    if (
      readOnlyFields.includes(name) &&
      name !== "PricePerUnit" &&
      name !== "IgstPercent" &&
      name !== "CgstPercent" &&
      name !== "SgstPercent"
    ) {
      return;
    }

    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: value };

      // Update related fields when ProjectID changes
      if (name === "ProjectID") {
        const selectedProject = allProjectList.find(
          (project) => project.ProjectID === value
        );
        updatedFormData.StrategicPartnerID = selectedProject
          ? selectedProject.StrategicPartnerID || ""
          : "";
      }

      // Calculate SubTotal and TotalAmount
      const pricePerUnit =
        name === "PricePerUnit"
          ? parseFloat(value) || 0
          : parseFloat(updatedFormData.PricePerUnit) || 0;
      const quantity = parseFloat(updatedFormData.Quantity) || 0;
      const igstPercent =
        name === "IgstPercent"
          ? parseFloat(value) || 0
          : parseFloat(updatedFormData.IgstPercent) || 0;
      const cgstPercent =
        name === "CgstPercent"
          ? parseFloat(value) || 0
          : parseFloat(updatedFormData.CgstPercent) || 0;
      const sgstPercent =
        name === "SgstPercent"
          ? parseFloat(value) || 0
          : parseFloat(updatedFormData.SgstPercent) || 0;

      // Calculate SubTotal: PricePerUnit * Quantity
      const subTotal = pricePerUnit * quantity;

      // Calculate taxes
      const igstAmount = (subTotal * igstPercent) / 100;
      const cgstAmount = (subTotal * cgstPercent) / 100;
      const sgstAmount = (subTotal * sgstPercent) / 100;

      // Calculate TotalAmount: SubTotal + IGST + CGST + SGST
      const totalAmount = subTotal + igstAmount + cgstAmount + sgstAmount;

      updatedFormData.SubTotal = isNaN(subTotal) ? "" : subTotal.toFixed(2);
      updatedFormData.TotalAmount = isNaN(totalAmount)
        ? ""
        : totalAmount.toFixed(2);

      return updatedFormData;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));

    // Fetch strategic partner info when ProjectID changes
    if (name === "ProjectID") {
      const selectedProject = allProjectList.find(
        (project) => project.ProjectID === value
      );
      if (selectedProject && selectedProject.StrategicPartnerID) {
        try {
          await getStrategicPartnerInfo(selectedProject.StrategicPartnerID);
          await getProductInfo(selectedProject.ProductID);
        } catch (error) {
          console.error("Error fetching strategic partner info:", error);
        }
      } else {
        // Clear strategicPartnerInfo if no valid StrategicPartnerID
        setStrategicPartnerInfo([]);
        setProductInfo([]);
        setFormData((prev) => ({
          ...prev,
          PlaceOfSupply: "",
          StrategicPartner: "",
          ShipTo: "",
          ProductName: "",
          Grade: "",
          HSCode: "",
        }));
      }
    }
  };

  return (
    <div className="row" style={{ padding: "20px" }}>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-4">
            <label htmlFor="ProjectID">Project</label>
            <FormControl fullWidth required error={!!errors.ProjectID}>
              <Select
                id="ProjectID"
                name="ProjectID"
                value={formData.ProjectID}
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>Select a Project</em>
                </MenuItem>
                {allProjectList.map((project) => (
                  <MenuItem key={project.ProjectID} value={project.ProjectID}>
                    {project.ProjectName}
                  </MenuItem>
                ))}
              </Select>
              {errors.ProjectID && (
                <FormHelperText>{errors.ProjectID}</FormHelperText>
              )}
            </FormControl>
          </div>
          <div className="col-md-4">
            <label htmlFor="InvoiceDate">Invoice Date</label>
            <TextField
              id="InvoiceDate"
              name="InvoiceDate"
              type="date"
              value={formData.InvoiceDate}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.InvoiceDate}
              helperText={errors.InvoiceDate}
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="StrategicPartner">Strategic Partner</label>
            <TextField
              id="StrategicPartner"
              name="StrategicPartner"
              value={formData.StrategicPartner}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.StrategicPartner}
              helperText={errors.StrategicPartner}
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="ProductName">Product Name</label>
            <TextField
              id="ProductName"
              name="ProductName"
              value={formData.ProductName}
              onChange={handleChange}
              fullWidth
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="Grade">Grade</label>
            <TextField
              id="Grade"
              name="Grade"
              value={formData.Grade}
              onChange={handleChange}
              fullWidth
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="HSCode">HS Code</label>
            <TextField
              id="HSCode"
              name="HSCode"
              value={formData.HSCode}
              onChange={handleChange}
              fullWidth
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="Quantity">Quantity</label>
            <TextField
              id="Quantity"
              name="Quantity"
              value={formData.Quantity}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.Quantity}
              helperText={errors.Quantity}
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="PlaceOfSupply">Place of Supply</label>
            <TextField
              id="PlaceOfSupply"
              name="PlaceOfSupply"
              value={formData.PlaceOfSupply}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.PlaceOfSupply}
              helperText={errors.PlaceOfSupply}
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="PO">PO</label>
            <TextField
              id="PO"
              name="PO"
              value={formData.PO}
              onChange={handleChange}
              fullWidth
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="PackagingNote">Packaging Note</label>
            <textarea
              id="PackagingNote"
              name="PackagingNote"
              value={formData.PackagingNote}
              onChange={handleChange}
              style={{
                width: "100%",
                height: "100px",
                padding: "10px",
                resize: "vertical",
              }}
            />
          </div>
          <div className="col-md-4">
            <label
              htmlFor="ShipTo"
              style={{ display: "block", marginBottom: "8px" }}
            >
              Ship To
            </label>
            <textarea
              id="ShipTo"
              name="ShipTo"
              value={formData.ShipTo}
              onChange={handleChange}
              style={{
                width: "100%",
                height: "100px",
                padding: "10px",
                resize: "vertical",
              }}
            />
          </div>
          <div className="col-md-4">
            <label
              htmlFor="DispatchFrom"
              style={{ display: "block", marginBottom: "8px" }}
            >
              Dispatch From
            </label>
            <textarea
              id="DispatchFrom"
              name="DispatchFrom"
              value={formData.DispatchFrom}
              onChange={handleChange}
              style={{
                width: "100%",
                height: "100px",
                padding: "10px",
                resize: "vertical",
              }}
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="EInvoiceAckNo">E-Invoice Ack No</label>
            <TextField
              id="EInvoiceAckNo"
              name="EInvoiceAckNo"
              value={formData.EInvoiceAckNo}
              onChange={handleChange}
              fullWidth
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="EInvoiceDate">E-Invoice Date</label>
            <TextField
              id="EInvoiceDate"
              name="EInvoiceDate"
              type="date"
              value={formData.EInvoiceDate}
              onChange={handleChange}
              fullWidth
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="Unit">Unit</label>
            <TextField
              id="Unit"
              name="Unit"
              value={formData.Unit}
              onChange={handleChange}
              fullWidth
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="PricePerUnit">Price Per Unit</label>
            <TextField
              id="PricePerUnit"
              name="PricePerUnit"
              type="number"
              value={formData.PricePerUnit}
              onChange={handleChange}
              fullWidth
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="SubTotal">Sub Total</label>
            <TextField
              id="SubTotal"
              name="SubTotal"
              type="number"
              value={formData.SubTotal}
              onChange={handleChange}
              fullWidth
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="IgstPercent">IGST Percent</label>
            <TextField
              id="IgstPercent"
              name="IgstPercent"
              type="number"
              value={formData.IgstPercent}
              onChange={handleChange}
              fullWidth
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="CgstPercent">CGST Percent</label>
            <TextField
              id="CgstPercent"
              name="CgstPercent"
              type="number"
              value={formData.CgstPercent}
              onChange={handleChange}
              fullWidth
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="SgstPercent">SGST Percent</label>
            <TextField
              id="SgstPercent"
              name="SgstPercent"
              type="number"
              value={formData.SgstPercent}
              onChange={handleChange}
              fullWidth
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="TotalAmount">Total Amount</label>
            <TextField
              id="TotalAmount"
              name="TotalAmount"
              type="number"
              value={formData.TotalAmount}
              onChange={handleChange}
              fullWidth
              disabled
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="EwayBillNo">Eway Bill No</label>
            <TextField
              id="EwayBillNo"
              name="EwayBillNo"
              value={formData.EwayBillNo}
              onChange={handleChange}
              fullWidth
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="VehicleNo">Vehicle No</label>
            <TextField
              id="VehicleNo"
              name="VehicleNo"
              value={formData.VehicleNo}
              onChange={handleChange}
              fullWidth
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="EInvoiceIRN">E-Invoice IRN</label>
            <TextField
              id="EInvoiceIRN"
              name="EInvoiceIRN"
              value={formData.EInvoiceIRN}
              onChange={handleChange}
              fullWidth
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProformaInvoiceForm;